import * as yup from 'yup'

import { OutlookReferralFormValues } from '../../pages/officeTaskPane/components/OutlookReferral/constants'
import {
  ClientDetailsInput,
  ClientDetailsInputFields,
} from '../../pages/referClient/steps/4_ClientDetails/constants'
import { getToCompanyContactName } from '../../pages/referClient/steps/5a_ConfirmReferral/helpers'
import { ClientEntity, CompanyEntity } from '../../types/entities'
import {
  CompanyTypeCode,
  REFERRAL_CONSENT_TYPE,
  ReferralConsentType,
  ReferralIndividual,
  ReferralType,
  ReferralTypeOption,
} from '../../types/misc'
import {
  CompanyTypeItem,
  ServiceAreaItem,
} from '../../types/responses/common-data'
import { CompassReportCollectionItem } from '../../types/responses/compass-reports'
import { ExistingAdviserReferralMetadataItem } from '../../types/responses/referral-metadata'
import {
  ReferralEmailItem,
  ReferralIndividualCollectionItem,
} from '../../types/responses/referrals'
import {
  ERROR_MESSAGES,
  EXPECTED_COMMISSION_AMOUNT_UNSURE,
  REFERRAL_TYPE,
  REFERRAL_TYPE_OPTION,
} from '../constants'
import {
  arrayToCommaSeparatedString,
  getAbbreviatedName,
  getFirstAndLastName,
  hasBothFirstAndLastName,
  startsWithVowel,
} from '../helpers/helperFunctions'
import { yupSchemas } from '../helpers/yupSchemas'
import companyTypeService from './companyTypeService'

interface OptionDetail {
  label: string
  description: string
}

class ReferralFlowService {
  hasClientConsent(consentType?: ReferralConsentType) {
    return !this.needsClientConsent(consentType)
  }

  needsClientConsent(consentType?: ReferralConsentType) {
    return consentType === REFERRAL_CONSENT_TYPE.yesESignature
  }

  getConsentType(
    metadata?: ExistingAdviserReferralMetadataItem,
  ): ReferralConsentType | undefined {
    return metadata?.shouldUseESignatureOption
      ? REFERRAL_CONSENT_TYPE.yesESignature
      : undefined
  }

  shouldCcClientOnInitialEmail(referralTypeOption?: ReferralTypeOption) {
    return referralTypeOption === REFERRAL_TYPE_OPTION.directCc
  }

  getReferralTypeOptionDetails(
    referralTypeOption: ReferralTypeOption,
  ): OptionDetail {
    const labels: Record<ReferralTypeOption, OptionDetail> = {
      directCc: {
        label: 'Email the professional, CC your client',
        description:
          'Send the professional your client’s details, with your client on copy.',
      },
      direct: {
        label: 'Email the professional',
        description:
          'Send the professional your client’s details so they can contact them.',
      },
      directPco: {
        label: 'Email the professional',
        description:
          'Send the professional your client’s details so they can contact them.',
      },
      indirect: {
        label: 'Email your client',
        description:
          'Send your client the details of the professional for them to schedule a call.',
      },
      bookCallForClient: {
        label: 'Book a call for your client',
        description:
          "Book a call directly in the professional's diary on your client's behalf.",
      },
    }

    const label = labels[referralTypeOption]

    if (!label) {
      throw new Error(`Unknown referral type option: ${referralTypeOption}`)
    }

    return label
  }

  normalizeReferralType(
    referralType?: ReferralTypeOption,
  ): ReferralType | undefined {
    if (!referralType) {
      return undefined
    }

    if (
      referralType === REFERRAL_TYPE_OPTION.directCc ||
      referralType === REFERRAL_TYPE_OPTION.direct ||
      referralType === REFERRAL_TYPE_OPTION.bookCallForClient ||
      referralType === REFERRAL_TYPE_OPTION.directPco
    ) {
      return REFERRAL_TYPE.direct
    }

    return referralType as ReferralType
  }

  normalizeReferralTypeOrFail(referralType?: ReferralTypeOption): ReferralType {
    const normalizedReferralType = this.normalizeReferralType(referralType)

    if (!normalizedReferralType) {
      throw new Error('Referral type is required')
    }

    return normalizedReferralType
  }

  buildUserEmailMessage(context: UserEmailMessageContext): string {
    const {
      clientDetails,
      reason,
      referralType,
      toCompany,
      toCompanyContactNames,
      selectedServiceAreas,
      compassReport,
      companyTypes,
      companyTypeCode,
      metadata,
    } = context

    const { firstName, lastName } = getFirstAndLastName(clientDetails?.fullName)
    const abbreviatedName = getAbbreviatedName(clientDetails?.fullName)

    const toCompanyContactName = getToCompanyContactName(toCompanyContactNames)
    const serviceAreaNames = selectedServiceAreas.map(
      (serviceArea) => serviceArea.name,
    )
    const serviceAreaNamesString = arrayToCommaSeparatedString(serviceAreaNames)
    const compassReportMessage = compassReport
      ? ` Please find background information on ${firstName}'s personal affairs in the attached snapshot. `
      : ' '

    const readableCompanyType = companyTypeService.getCompanyTypeByCodeOrFail(
      companyTypes,
      companyTypeCode,
    )

    const isInternalRelationship = !!metadata.panel?.internalRelationship

    const sharedMessages: Partial<Record<ReferralTypeOption, string[]>> = {
      direct: [
        `Dear ${toCompanyContactName},\n\n`,
        `Our client, ${abbreviatedName} is in need of advice regarding ${serviceAreaNamesString}.${compassReportMessage}Are you able to please reach out to them on the below number / email address?`,
        reason?.length ? `\n\n${reason}` : '',
      ],
      directPco: [
        `Dear ${firstName},\n\n`,
        'In order to get a Will organised, we have passed your details to Private Client Online. You will hear from them shortly.',
      ],
    }

    const externalRelationshipMessages: Partial<
      Record<ReferralTypeOption, string[]>
    > = {
      ...sharedMessages,
      directCc: [
        `Dear ${toCompanyContactName},\n\n`,
        `${firstName} ${lastName} (cc'd) is after some advice on ${serviceAreaNamesString}.${compassReportMessage}Can I leave it with you to please reach out to ${firstName}?`,
        reason?.length ? `\n\n${reason}` : '',
      ],
      indirect: [
        `Dear ${firstName},\n\n`,
        `You have requested advice concerning ${serviceAreaNamesString}. Please see below for details of ${
          startsWithVowel(readableCompanyType.name) ? 'an' : 'a'
        } ${
          readableCompanyType.name
        } that I think will be able to assist. Feel free to schedule a call with them using the below 'Schedule a conversation' link.`,
      ],
    }

    const internalRelationshipMessages: Partial<
      Record<ReferralTypeOption, string[]>
    > = {
      ...sharedMessages,
      directCc: [
        `Dear ${toCompanyContactName},\n\n`,
        `${firstName} ${lastName} (cc'd) is after some advice on ${serviceAreaNamesString}, this is to introduce you to my ${toCompany.presentationName} colleagues.`,
        compassReportMessage,
        reason?.length ? `\n\n${reason}` : '',
      ],
      indirect: [
        `Dear ${firstName},\n\n`,
        `You have requested advice concerning ${serviceAreaNamesString}, this is to introduce you to my ${toCompany.presentationName} colleagues. Feel free to schedule a call with them using the below 'Schedule a conversation' link.`,
      ],
    }

    const messages = isInternalRelationship
      ? internalRelationshipMessages
      : externalRelationshipMessages

    const message = messages[referralType]

    if (!message) {
      throw new Error(`No message found for referral type: ${referralType}`)
    }

    return message.join('')
  }

  getCommonReferralFlowValidationSchema(options: {
    existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
  }) {
    const { existingAdviserReferralMetadata } = options

    return {
      // Client details questions
      [ClientDetailsInputFields.fullName]: yup
        .string()
        .required("The client's name is required")
        .test(
          'is-valid-full-name',
          'Please provide both a first and last name',
          hasBothFirstAndLastName,
        ),

      [ClientDetailsInputFields.email]: yup
        .string()
        .email('Must be a valid email')
        .required("The client's email is required"),

      [ClientDetailsInputFields.dateOfBirth]: yupSchemas.dayMonthYear(),

      // Commission questions
      [ClientDetailsInputFields.isExpectingCommission]: yup
        .boolean()
        .test(
          'is-expecting-commission-question,',
          ERROR_MESSAGES.required,
          (value: unknown) => {
            if (
              existingAdviserReferralMetadata?.shouldShowIsExpectingCommissionQuestion
            ) {
              return typeof value === 'boolean'
            }

            return true
          },
        ),

      [ClientDetailsInputFields.knowsCommissionAmount]: yup
        .boolean()
        .test(
          'knows-commission-amount',
          ERROR_MESSAGES.required,
          (value, context) => {
            const formValues = context.parent as OutlookReferralFormValues

            if (formValues.isExpectingCommission) {
              return typeof value === 'boolean'
            }

            return true
          },
        ),

      [ClientDetailsInputFields.expectedCommissionAmount]: yup
        .mixed()
        .test(
          'expected-commission-amount',
          ERROR_MESSAGES.required,
          (_value, context) => {
            const formValues = context.parent as ClientDetailsInput

            if (
              formValues.isExpectingCommission &&
              formValues.knowsCommissionAmount
            ) {
              return !!formValues.expectedCommissionAmount
            }

            return true
          },
        )
        .test(
          'is-valid-money-amount-or-negative-one',
          'Must be a valid money amount or Unsure',
          (value) => {
            const validMoneySchema = yupSchemas.validMoneyValue()

            return (
              validMoneySchema.isValidSync(value) ||
              value === EXPECTED_COMMISSION_AMOUNT_UNSURE
            )
          },
        ),
    }
  }

  makeReferralIndividual(
    individual: ReferralIndividualCollectionItem,
  ): ReferralIndividual {
    return {
      companyUserId: individual.companyUser.id,
      userId: individual.user.id,
      email: individual.user.email,
      fullName: individual.user.fullName,
      company: individual.company,
    }
  }
}

const referralFlowService = new ReferralFlowService()

export default referralFlowService

export interface UserEmailMessageContext {
  clientDetails: Pick<ClientEntity, 'fullName'>
  reason?: string
  referralType: ReferralTypeOption
  toCompany: Pick<CompanyEntity, 'presentationName'>
  toCompanyContactNames: ReferralEmailItem['toNames']
  selectedServiceAreas: ServiceAreaItem[]
  compassReport?: CompassReportCollectionItem
  companyTypes: CompanyTypeItem[]
  companyTypeCode: CompanyTypeCode
  metadata: ExistingAdviserReferralMetadataItem
}
